import React, { useEffect, useState } from 'react';
import {
  Grid,
  Typography,
  Card,
  CardContent,
  Link,
  Box,
  Chip,
  Stack,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  CheckCircle,
  HourglassEmpty,
  Cancel,
  Verified,
  Description,
} from '@mui/icons-material';
import axios from 'axios';
import config from '../../../apiServices/appConfig';

const { BASE_URL, API_VERSION } = config;

interface Client {
  client_id: string;
  email: string;
  name: string;
  status: string;
  email_verified: boolean;
  check_kyc: boolean;
  kyb_status: string;
  created_at: string;
  updated_at: string;
}

interface KYBDoc {
  kyb_id: string;
  document_type: string;
  s3_url: string;
  kyb_status: string;
  remarks: string;
  created_at: string;
}

const ReviewStep: React.FC = () => {
  const [client, setClient] = useState<Client | null>(null);
  const [kybDocs, setKybDocs] = useState<KYBDoc[]>([]);
  const [loading, setLoading] = useState(true);

  const getManualKyb = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${BASE_URL}${API_VERSION}/kyb`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const { client, kybData } = response?.data?.data?.data || {};
      if (client && client.length > 0) setClient(client[0]);
      if (kybData) setKybDocs(kybData);
    } catch (err) {
      console.error('Error fetching KYB data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getManualKyb();
  }, []);

  const renderChip = (label: string, value: boolean | string | undefined) => {
    if (value === true || value === 'success' || value === 'active') {
      return (
        <Chip
          icon={<CheckCircle />}
          label={label}
          color="success"
          variant="outlined"
        />
      );
    }
    if (value === false || value === 'failed' || value === 'inactive') {
      return (
        <Chip
          icon={<Cancel />}
          label={`${label} - Failed`}
          color="error"
          variant="outlined"
        />
      );
    }
    return (
      <Chip
        icon={<HourglassEmpty />}
        label={`${label} - Pending`}
        color="warning"
        variant="outlined"
      />
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (!client) {
    return (
      <Typography color="text.secondary" align="center" mt={4}>
        No KYB data found.
      </Typography>
    );
  }

  return (
    <Grid container spacing={3}>
      {/* --- CLIENT SECTION --- */}
      <Grid item xs={12}>
        <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Client Information
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Typography><b>Name:</b> {client.name}</Typography>
            <Typography><b>Email:</b> {client.email}</Typography>
            <Typography><b>Client ID:</b> {client.client_id}</Typography>
            <Typography><b>Created:</b> {new Date(client.created_at).toLocaleString()}</Typography>

            <Box mt={2}>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {renderChip('Email Verified', client.email_verified)}
                {renderChip('User Status', client.status)}
                {renderChip('KYC Checked', client.check_kyc)}
                {renderChip('KYB Status', client.kyb_status)}
                {client.kyb_status === 'success' && (
                  <Chip
                    icon={<Verified />}
                    label="Auto Approved"
                    color="primary"
                    variant="filled"
                  />
                )}
              </Stack>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* --- KYB DOCUMENT SECTION --- */}
      <Grid item xs={12}>
        <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              KYB Documents
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {kybDocs.length === 0 ? (
              <Typography color="text.secondary">No documents uploaded yet.</Typography>
            ) : (
              kybDocs.map((doc) => (
                <Box
                  key={doc.kyb_id}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    p: 1.5,
                    mb: 1,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    border: '1px solid #e0e0e0',
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <Description color="action" />
                    <Typography>
                      <b>{doc.document_type.toUpperCase()}</b>
                    </Typography>
                    {renderChip('Status', doc.kyb_status)}
                  </Box>
                  <Link
                    href={doc.s3_url}
                    target="_blank"
                    underline="hover"
                    sx={{ fontWeight: 500 }}
                  >
                    View Document
                  </Link>
                </Box>
              ))
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default ReviewStep;
